import React, { useState, useEffect } from "react";
import { useSnackbar } from "../../context/SnackbarContext";
import {
  createEmployeeIncidentReportApi,
  getEmployeeIncidentReportsApi,
  getEmergencyContactsApi,
} from "../../services/axios/AuthApis/IncidentReportApis";
import { INCIDENT_CATEGORIES_WITH_OPTIONS } from "../../constants/AppConstants";
import {
  ExclamationTriangleIcon,
  EnvelopeIcon,
  ClockIcon,
  PhoneIcon,
  PaperAirplaneIcon,
  CalendarIcon,
  ShieldExclamationIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import SubmitConfirmDialog from "./SubmitConfirmDialog";
import ChangeTypeConfirmDialog from "./ChangeTypeConfirmDialog";
import PreviousReportsSection from "./PreviousReportsSection";
import UploadFileModal from "../ui/UploadFileModal";
import ApiLoader from "../ui/ApiLoader";

const EndUserIncidentReporting = () => {
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previousReports, setPreviousReports] = useState([]);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [form, setForm] = useState({
    incident_type: "",
    incident_subtype: "",
    priority: "medium",
    description: "",
  });

  const [showUploadFile, setShowUploadFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [attachments, setAttachments] = useState([]);

  const [pendingChange, setPendingChange] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  // Load previous reports and emergency contacts
  useEffect(() => {
    loadPreviousReports();
    loadEmergencyContacts();
  }, []);

  const loadEmergencyContacts = async () => {
    try {
      setLoadingContacts(true);
      const customerId = user?.employee_details?.customer_id || user?.customer_master_id || "";
      if (!customerId) return;
      const data = await getEmergencyContactsApi(customerId);
      setEmergencyContacts(data || []);
    } catch (error) {
      console.error("Error loading emergency contacts:", error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadPreviousReports = async () => {
    try {
      setLoading(true);
      const reporter = user?.email || user?.firstname || "";
      const data = await getEmployeeIncidentReportsApi(reporter);
      setPreviousReports(data.results || data || []);
    } catch (error) {
      console.error("Error loading previous reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.description.trim()) {
      showSnackbar("Please provide an incident description", "error");
      return;
    }
    if (!form.incident_type) {
      showSnackbar("Please select an incident type", "error");
      return;
    }
    // Show confirmation dialog with details before submitting
    setShowSubmitConfirm(true);
  };

  const handleUploadFile = () => {
    if (selectedFile instanceof File) {
      setAttachments(prev => [...prev, selectedFile]);
      setSelectedFile(null);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const confirmSubmit = async () => {
    setShowSubmitConfirm(false);
    try {
      setSubmitting(true);

      // Find the category name from INCIDENT_CATEGORIES_WITH_OPTIONS
      const selectedCategory = INCIDENT_CATEGORIES_WITH_OPTIONS.find(cat => cat.id === form.incident_type);
      const categoryName = selectedCategory?.name || form.incident_type;

      const hasFiles = attachments.some(f => f instanceof File);
      let incidentData;

      if (hasFiles) {
        incidentData = new FormData();
        incidentData.append("title", categoryName);
        incidentData.append("description", form.description);
        // Store the category ID so admin filters work; subtype goes in affected_systems
        incidentData.append("incident_type", form.incident_type);
        incidentData.append("severity", form.priority);
        incidentData.append("status", "open");
        incidentData.append("reporter", user?.email || user?.firstname || "end_user");
        incidentData.append("affected_systems", form.incident_subtype || "");
        attachments.forEach(file => {
          if (file instanceof File) incidentData.append("attachments", file);
        });
      } else {
        incidentData = {
          title: categoryName,
          description: form.description,
          // Store the category ID so admin filters work; subtype goes in affected_systems
          incident_type: form.incident_type,
          severity: form.priority,
          status: "open",
          reporter: user?.email || user?.firstname || "end_user",
          affected_systems: form.incident_subtype || "",
        };
      }

      await createEmployeeIncidentReportApi(incidentData);
      showSnackbar("Incident report submitted successfully!", "success");
      setForm({
        incident_type: "",
        incident_subtype: "",
        priority: "medium",
        description: "",
      });
      setAttachments([]);
      loadPreviousReports();
    } catch (error) {
      console.error("Error submitting incident:", error);
      showSnackbar(error?.response?.data?.detail || error?.message || "Error submitting incident report", "error");
    } finally {
      setSubmitting(false);
    }
  };


  const getSeverityBadge = (severity) => {
    const styles = {
      low: "bg-blue-100 dark:bg-blue-900 text-white",
      medium: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
      high: "bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200",
      critical: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
    };
    return styles[severity] || "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200";
  };

  const getStatusBadge = (status) => {
    const styles = {
      reported: "bg-blue-100 dark:bg-blue-900 text-white",
      investigating: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
      contained: "bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200",
      resolved: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
      closed: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200",
      false_alarm: "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400",
    };
    return styles[status] || "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200";
  };

  const handleIncidentTypeChange = (categoryId, subtype) => {
    // If a different category is already selected (not just empty), confirm
    if (form.incident_type && form.incident_type !== categoryId) {
      setPendingChange({ categoryId, subtype });
      setShowConfirm(true);
    } else {
      setForm({ ...form, incident_type: categoryId, incident_subtype: subtype });
    }
  };

  const confirmTypeChange = () => {
    if (pendingChange) {
      setForm({ ...form, incident_type: pendingChange.categoryId, incident_subtype: pendingChange.subtype });
    }
    setPendingChange(null);
    setShowConfirm(false);
  };

  const cancelTypeChange = () => {
    setPendingChange(null);
    setShowConfirm(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-CA"); // YYYY-MM-DD format
  };

  return (
    <div className="py-6 px-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          Incident Reporting
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Report any security concerns or suspicious activities to protect our organization
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Report Form */}
        <div className="lg:col-span-2">
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {/* Submit overlay loader */}
            {submitting && (
              <ApiLoader variant="overlay" label="Submitting your report..." />
            )}
            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              Report New Incident
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Incident Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-center mb-4">
                  Select Incident Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {INCIDENT_CATEGORIES_WITH_OPTIONS.map((category) => (
                    <div key={category.id}>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5 text-center">
                        {category.name}
                      </label>
                      <select
                        value={form.incident_type === category.id ? form.incident_subtype : ""}
                        onChange={(e) => handleIncidentTypeChange(category.id, e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          form.incident_type === category.id
                            ? "border-blue-500 ring-1 ring-blue-400"
                            : "border-gray-300 dark:border-gray-600"
                        }`}
                      >
                        <option value="">{category.placeholder}</option>
                        {category.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Priority Level */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-center mb-3">
                  Priority Level
                </label>
                <div className="flex items-center justify-center space-x-6">
                  {["low", "medium", "high", "critical"].map((level) => (
                    <label key={level} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="priority"
                        value={level}
                        checked={form.priority === level}
                        onChange={(e) => setForm({ ...form, priority: e.target.value })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">{level}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Incident Description */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-center mb-2">
                  Incident Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Please provide detailed information about the incident including when it occurred, what happened, and any other relevant details..."
                  required
                />
              </div>

              {/* Attachments */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Attachments <span className="text-gray-400 text-xs font-normal">(optional)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowUploadFile(true)}
                    className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    <PaperClipIcon className="w-4 h-4" />
                    Attach File
                  </button>
                </div>
                {attachments.length > 0 && (
                  <ul className="space-y-2">
                    {attachments.map((file, index) => (
                      <li key={index} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 text-sm">
                        <span className="truncate text-gray-700 dark:text-gray-300">{file?.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-400 hover:text-red-500 flex-shrink-0"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {attachments.length === 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">Screenshots, logs, or other supporting documents</p>
                )}
              </div>

              {/* Submit Button */}
              {submitting && <ApiLoader variant="bar" className="mb-3" />}
              <button
                type="submit"
                disabled={submitting || !form.description.trim() || !form.incident_type}
                className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                {submitting ? "Submitting..." : "Submit Incident Report"}
              </button>
            </form>
          </div>
        </div>

        {/* Right Column - Emergency Contacts & Guidelines */}
        <div className="space-y-6">
          {/* Emergency Contacts */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white text-center mb-4">
              Emergency Contacts
            </h3>

            {loadingContacts ? (
              <ApiLoader label="Loading contacts..." />
            ) : emergencyContacts.length > 0 ? (
              <div className="space-y-5">
                {emergencyContacts.map((contact, idx) => (
                  <div key={contact.id || idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white text-center mb-3">
                      {contact.type}
                    </h4>
                    <div className="space-y-2">
                      {contact.employee_name && (
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <span className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-600 inline-flex items-center justify-center text-xs">👤</span>
                          <span>{contact.employee_name}</span>
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-600" />
                        {contact.employee_email ? (
                          <a href={`mailto:${contact.employee_email}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                            {contact.employee_email}
                          </a>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-600">Not assigned</span>
                        )}
                      </div>
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <PhoneIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-600" />
                        {contact.contact_no ? (
                          <a href={`tel:${contact.contact_no}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                            {contact.contact_no}
                          </a>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-600">Not set</span>
                        )}
                      </div>
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <ClockIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-600" />
                        {contact.time || "Not specified"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                <p className="text-sm">No emergency contacts configured.</p>
                <p className="text-xs mt-1">Contact your administrator to set up emergency contacts.</p>
              </div>
            )}
          </div>

          {/* Important Notice */}
          <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-xl p-5">
            <h4 className="text-red-800 dark:text-red-200 font-bold text-center mb-2">Important Notice</h4>
            <p className="text-red-700 dark:text-red-300 text-sm text-center">
              For immediate security threats or ongoing attacks, call our emergency line immediately.
              Do not delay reporting critical incidents.
            </p>
          </div>

          {/* Reporting Guidelines */}
          <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-800 rounded-xl p-5">
            <h4 className="text-yellow-800 dark:text-yellow-200 font-bold text-center mb-3">Reporting Guidelines</h4>
            <ul className="text-yellow-700 dark:text-yellow-200 text-sm space-y-1 text-center">
              <li>• Report incidents immediately</li>
              <li>• Provide as much detail as possible</li>
              <li>• Do not investigate on your own</li>
              <li>• Preserve evidence when safe to do so</li>
              <li>• Follow up if you don't receive confirmation</li>
            </ul>
          </div>
        </div>
      </div>

      <PreviousReportsSection
        loading={loading}
        previousReports={previousReports}
        getSeverityBadge={getSeverityBadge}
        getStatusBadge={getStatusBadge}
        formatDate={formatDate}
      />

      <SubmitConfirmDialog
        show={showSubmitConfirm}
        form={form}
        user={user}
        attachments={attachments}
        submitting={submitting}
        onConfirm={confirmSubmit}
        onCancel={() => setShowSubmitConfirm(false)}
      />

      <ChangeTypeConfirmDialog
        show={showConfirm}
        currentTypeId={form.incident_type}
        currentSubtype={form.incident_subtype}
        newTypeId={pendingChange?.categoryId}
        newSubtype={pendingChange?.subtype}
        onConfirm={confirmTypeChange}
        onCancel={cancelTypeChange}
      />

      <UploadFileModal
        showUploadFile={showUploadFile}
        setShowUploadFile={setShowUploadFile}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        handleUploadFile={handleUploadFile}
        title="Attach File"
        subtitle="Upload screenshots, logs, or other supporting documents"
      />
    </div>
  );
};

export default EndUserIncidentReporting;
